import { useState, useEffect, useCallback } from 'react';
import { useAgent } from '../../../context';
import { getAgentTools, updateAgentTools, removeToolFromAgent } from '../../../../../../services/tools';
import { getToolsList } from '../../../../../../services/tools';

/**
 * Hook para manejar la lógica de ToolsPage
 * 
 * - Fetch de tools del agente
 * - Fetch de tools disponibles
 * - Operaciones CRUD (add, remove, toggle, update settings)
 * - Manejo de slideouts
 */
const useToolsPage = () => {
  const { agentId, loading: agentLoading } = useAgent();
  
  // Estados de tools
  const [installedTools, setInstalledTools] = useState([]);
  const [availableTools, setAvailableTools] = useState([]);
  
  // Estados de loading
  const [loading, setLoading] = useState(false);
  const [loadingToolsList, setLoadingToolsList] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Estados de error
  const [error, setError] = useState(null);
  
  // Estados de UI (slideouts)
  const [showSlideoutTools, setShowSlideoutTools] = useState(false);
  const [showConfigurationSlideout, setShowConfigurationSlideout] = useState(false);
  const [selectedToolForConfiguration, setSelectedToolForConfiguration] = useState(null);
  const [isFromToolsLibrary, setIsFromToolsLibrary] = useState(false);
  
  // Estados de alertas
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  
  // Fetch de tools del agente
  const fetchAgentTools = useCallback(async () => {
    if (!agentId || agentLoading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getAgentTools(agentId);
      const tools = response?.data?.data || [];
      setInstalledTools(Array.isArray(tools) ? tools : []);
    } catch (err) {
      console.error('Error fetching agent tools:', err);
      setError(err);
      setInstalledTools([]);
    } finally {
      setLoading(false);
    }
  }, [agentId, agentLoading]);
  
  // Fetch de tools disponibles (para el slideout)
  const fetchToolsList = useCallback(async () => {
    setLoadingToolsList(true);
    
    try {
      const response = await getToolsList();
      const tools = response?.data?.data || [];
      setAvailableTools(Array.isArray(tools) ? tools : []);
    } catch (err) {
      console.error('Error fetching tools list:', err);
      setAvailableTools([]);
    } finally {
      setLoadingToolsList(false);
    }
  }, []);
  
  // Cargar tools del agente cuando cambia el agentId
  useEffect(() => {
    if (agentId && !agentLoading) {
      fetchAgentTools();
    }
  }, [agentId, agentLoading, fetchAgentTools]);
  
  // Cargar tools disponibles cuando se abre el slideout
  useEffect(() => {
    if (showSlideoutTools && availableTools.length === 0) {
      fetchToolsList();
    }
  }, [showSlideoutTools, availableTools.length, fetchToolsList]);
  
  // Toggle tool (habilitar/deshabilitar)
  const handleToggleTool = useCallback(async (toolId) => {
    if (!agentId || isUpdating) return;
    
    setIsUpdating(true);
    
    try {
      // Optimistic update
      const updatedTools = installedTools.map(tool =>
        tool.id === toolId ? { ...tool, isEnabled: !tool.isEnabled } : tool
      );
      setInstalledTools(updatedTools);
      
      // Llamar al servicio
      await updateAgentTools(agentId, { tools: updatedTools });
      
      showNotification('Tool updated successfully', 'success');
    } catch (err) {
      console.error('Error toggling tool:', err);
      // Revertir cambios
      fetchAgentTools();
      showNotification('Failed to update tool. Please try again.', 'error');
    } finally {
      setIsUpdating(false);
    }
  }, [agentId, installedTools, isUpdating, fetchAgentTools]);
  
  // Eliminar tool
  const handleDeleteTool = useCallback(async (toolId) => {
    if (!agentId || isUpdating) return;
    
    setIsUpdating(true);
    
    try {
      // Optimistic update
      const updatedTools = installedTools.filter(tool => tool.id !== toolId);
      setInstalledTools(updatedTools);
      
      // Llamar al servicio
      await removeToolFromAgent(agentId, toolId);
      
      showNotification('Tool removed successfully', 'success');
    } catch (err) {
      console.error('Error removing tool:', err);
      // Revertir cambios
      fetchAgentTools();
      showNotification('Failed to remove tool. Please try again.', 'error');
    } finally {
      setIsUpdating(false);
    }
  }, [agentId, installedTools, isUpdating, fetchAgentTools]);
  
  // Agregar tools
  const handleAddTools = useCallback(async (newTools) => {
    if (!agentId || !newTools?.length || isUpdating) return;
    
    setIsUpdating(true);
    
    try {
      const toolsToAdd = newTools.map((tool) => ({
        id: tool.id || tool.action_uuid || tool.tool_id,
        title: tool.title || tool.name,
        description: tool.description || '',
        icon: tool.icon || '',
        isEnabled: true,
        tag: tool.tag || 'Ready to use',
        tagColor: tool.tagColor || 'informative',
        settings: tool.settings || {},
        type: tool.type || '',
        category: tool.category || tool.categories || ''
      }));
      
      // Optimistic update
      const updatedTools = [...installedTools, ...toolsToAdd];
      setInstalledTools(updatedTools);
      
      // Llamar al servicio
      await updateAgentTools(agentId, { tools: updatedTools });
      
      setShowSlideoutTools(false);
      showNotification('Tools added successfully', 'success');
    } catch (err) {
      console.error('Error adding tools:', err);
      // Revertir cambios
      fetchAgentTools();
      showNotification('Failed to add tools. Please try again.', 'error');
    } finally {
      setIsUpdating(false);
    }
  }, [agentId, installedTools, isUpdating, fetchAgentTools]);
  
  // Actualizar configuración de tool
  const handleUpdateToolSettings = useCallback(async (toolId, settings) => {
    if (!agentId || isUpdating) return;
    
    setIsUpdating(true);
    
    try {
      // Optimistic update
      const updatedTools = installedTools.map(tool =>
        tool.id === toolId
          ? { ...tool, settings: { ...tool.settings, ...settings } }
          : tool
      );
      setInstalledTools(updatedTools);
      
      // Llamar al servicio
      await updateAgentTools(agentId, { tools: updatedTools });
      
      setShowConfigurationSlideout(false);
      setSelectedToolForConfiguration(null);
      showNotification('Tool configuration saved successfully', 'success');
    } catch (err) {
      console.error('Error updating tool settings:', err);
      // Revertir cambios
      fetchAgentTools();
      showNotification('Failed to update tool configuration. Please try again.', 'error');
    } finally {
      setIsUpdating(false);
    }
  }, [agentId, installedTools, isUpdating, fetchAgentTools]);
  
  // Handlers de slideouts
  const handleOpenSlideoutTools = useCallback(() => {
    setShowSlideoutTools(true);
  }, []);
  
  const handleCloseSlideoutTools = useCallback(() => {
    setShowSlideoutTools(false);
  }, []);
  
  const handleOpenConfiguration = useCallback((tool, fromToolsLibrary = false) => {
    setSelectedToolForConfiguration(tool);
    setIsFromToolsLibrary(fromToolsLibrary);
    setShowConfigurationSlideout(true);
  }, []);
  
  const handleCloseConfiguration = useCallback(() => {
    setShowConfigurationSlideout(false);
    setSelectedToolForConfiguration(null);
    setIsFromToolsLibrary(false);
  }, []);
  
  const handleGoBackToToolsLibrary = useCallback(() => {
    setShowConfigurationSlideout(false);
    setSelectedToolForConfiguration(null);
    setIsFromToolsLibrary(false);
  }, []);
  
  const handleSaveConfiguration = useCallback(async (configurationData) => {
    if (selectedToolForConfiguration?.id) {
      await handleUpdateToolSettings(selectedToolForConfiguration.id, configurationData);
    }
  }, [selectedToolForConfiguration, handleUpdateToolSettings]);
  
  // Función helper para mostrar notificaciones
  const showNotification = useCallback((message, type = 'success') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
  }, []);
  
  const handleCloseAlert = useCallback(() => {
    setShowAlert(false);
    setAlertMessage('');
  }, []);
  
  return {
    // Estado
    installedTools,
    availableTools,
    loading,
    loadingToolsList,
    isUpdating,
    error,
    
    // Slideouts
    showSlideoutTools,
    showConfigurationSlideout,
    selectedToolForConfiguration,
    isFromToolsLibrary,
    
    // Alertas
    showAlert,
    alertMessage,
    alertType,
    
    // Handlers
    handleToggleTool,
    handleDeleteTool,
    handleAddTools,
    handleOpenSlideoutTools,
    handleCloseSlideoutTools,
    handleOpenConfiguration,
    handleCloseConfiguration,
    handleGoBackToToolsLibrary,
    handleSaveConfiguration,
    handleCloseAlert,
  };
};

export default useToolsPage;

